import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useIsMobile } from '../../hooks/use-is-mobile';

import { useGetAllClubQuery } from '../../redux';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';
import { useAuthContext } from '../../context/auth-context';

const SEARCH_DEBOUNCE_MS = 400;

const ZONE_COLORS = [
  { bg: '#e8f4e8', color: '#1e5c30' },
  { bg: '#e8f0fb', color: '#1d3f89' },
  { bg: '#fdf3e3', color: '#7d4e2d' },
  { bg: '#f5eef8', color: '#6c3483' },
  { bg: '#fef9e7', color: '#7d6608' },
  { bg: '#fdecea', color: '#922b21' },
];

function getZoneColors(zone, affiliations) {
  if (!zone) return { bg: '#f0f0f0', color: '#555' };
  const match = zone.match(/\d+/);
  if (match) {
    const idx = (parseInt(match[0], 10) - 1) % ZONE_COLORS.length;
    return ZONE_COLORS[Math.max(0, idx)];
  }
  const idx = affiliations.findIndex(a => a.zone === zone);
  return ZONE_COLORS[Math.max(0, idx % ZONE_COLORS.length)];
}

function ZoneBadge({ zone, affiliations }) {
  if (!zone) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const { bg, color } = getZoneColors(zone, affiliations);
  return (
    <Chip
      label={zone}
      size="small"
      sx={{
        bgcolor: bg,
        color,
        fontWeight: 500,
        fontSize: '0.72rem',
        height: 22,
        border: 'none',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function StatusDot({ active }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: active ? '#1e5c30' : '#bbb',
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" color={active ? 'text.primary' : 'text.disabled'}>
        {active ? 'Activo' : 'Inactivo'}
      </Typography>
    </Box>
  );
}

const INITIAL_FILTERS = { name: '', affiliatedTo: '', status: '' };

export const ClubTable = React.memo(function ClubTable({ onEdit, onDelete, selectedId }) {
  const { activeCamp, hasPermission, effectiveZone } = useAuthContext();
  // `clubs.view` deja ver el listado; editar y borrar exigen `clubs.manage`, que un
  // Secretario de Zona no tiene. El servidor ya lo rechaza; esto no lo ofrece.
  const puedeGestionar = hasPermission('clubs.manage');
  const campId = activeCamp?._id ?? null;
  const isMobile = useIsMobile();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selected, setSelected] = useState([]);

  const { data: affiliations = [] } = useGetAllAffiliationsQuery(
    campId ? { campId } : undefined,
    { skip: !campId }
  );

  useEffect(() => {
    setPage(0);
    setSearchInput('');
    setFilters(INITIAL_FILTERS);
    setSelected([]);
  }, [campId]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setFilters(curr => ({ ...curr, name: searchInput }));
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const { data, isLoading, isFetching } = useGetAllClubQuery(
    {
      page: page + 1,
      limit: rowsPerPage,
      campId,
      name: filters.name || undefined,
      affiliatedTo: filters.affiliatedTo || undefined,
      status: filters.status !== '' ? filters.status : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const clubs = data?.results || [];
  const total = data?.pagination?.total || 0;

  const handleSelectAll = e => {
    setSelected(e.target.checked ? clubs.map(c => c._id) : []);
  };

  const handleSelectOne = id => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const allSelected = clubs.length > 0 && selected.length === clubs.length;
  const someSelected = selected.length > 0 && selected.length < clubs.length;

  const uniqueZoneCount = new Set(
    clubs.map(c => (typeof c.affiliatedTo === 'object' ? c.affiliatedTo?.zone : null)).filter(Boolean)
  ).size;

  /**
   * Para un Secretario de Zona el filtro de zona no es una elección: el servidor ya le
   * devuelve solo sus clubes (`zoneScope`), así que ofrecerle las otras siete zonas del
   * campamento sería mentirle — elegiría y no cambiaría nada. Mismo criterio que el
   * servidor y que `AffiliationFilter`: tener zona y no poder ver todas.
   *
   * `effectiveZone` y no `user.zone`, para que bajo "Ver como sec · Zona X" el filtro
   * diga lo mismo que están devolviendo los datos.
   */
  const zonaFija = Boolean(effectiveZone) && !hasPermission('reports.view-all-zones');
  const zoneOptions = useMemo(
    () => (zonaFija ? affiliations.filter(a => a.zone === effectiveZone) : affiliations),
    [affiliations, zonaFija, effectiveZone]
  );

  // Con una sola afiliación en la zona el filtro se fija en ella. Si hubiera varias con
  // el mismo nombre de zona se deja "Todas": el acotado del servidor cubre justo ese
  // conjunto, y fijar una escondería los clubes de las otras.
  const zonaUnica = zonaFija && zoneOptions.length === 1 ? zoneOptions[0]._id : null;
  useEffect(() => {
    if (zonaUnica && filters.affiliatedTo !== zonaUnica) {
      setFilters(f => ({ ...f, affiliatedTo: zonaUnica }));
      setPage(0);
    }
  }, [zonaUnica, filters.affiliatedTo]);

  const hasFilters = searchInput || filters.affiliatedTo || filters.status;

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { sm: 'wrap' },
          gap: 1.5,
          mb: 2,
          alignItems: { sm: 'center' },
        }}
      >
        <TextField
          placeholder="Buscar por nombre…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 220 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 160 } }}>
          <InputLabel>Zona</InputLabel>
          <Select
            value={filters.affiliatedTo}
            onChange={e => {
              setFilters(f => ({ ...f, affiliatedTo: e.target.value }));
              setPage(0);
            }}
            label="Zona"
            disabled={zonaFija}
          >
            {(!zonaFija || zoneOptions.length !== 1) && <MenuItem value="">Todas</MenuItem>}
            {zoneOptions.map(aff => (
              <MenuItem key={aff._id} value={aff._id}>
                {aff.name}{aff.zone ? ` — ${aff.zone}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 120 } }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={filters.status}
            onChange={e => {
              setFilters(f => ({ ...f, status: e.target.value }));
              setPage(0);
            }}
            label="Estado"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="true">Activo</MenuItem>
            <MenuItem value="false">Inactivo</MenuItem>
          </Select>
        </FormControl>

        {hasFilters && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSearchInput('');
              setFilters(INITIAL_FILTERS);
              setPage(0);
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Limpiar
          </Button>
        )}

        <Box sx={{ ml: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            {total} {total === 1 ? 'club' : 'clubes'}
            {uniqueZoneCount > 0 && ` · ${uniqueZoneCount} zona${uniqueZoneCount === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Box>

      {/* Mobile: card list */}
      {isMobile && (
        <Stack spacing={1.5}>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} variant='outlined'>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Skeleton variant='text' height={22} width='60%' />
                    <Skeleton variant='text' height={18} width='40%' sx={{ mt: 0.5 }} />
                  </CardContent>
                </Card>
              ))
            : clubs.map(club => {
                const zone = typeof club.affiliatedTo === 'object' ? club.affiliatedTo?.zone : null;
                return (
                  <Card key={club._id} variant='outlined' sx={{ borderColor: selectedId === club._id ? '#284B32' : undefined }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant='subtitle2' fontWeight={700}>{club.name}</Typography>
                        <StatusDot active={club.status} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ZoneBadge zone={zone} affiliations={affiliations} />
                        <Typography variant='caption' color='text.secondary'>
                          {club.director || '—'}
                        </Typography>
                      </Box>
                      {puedeGestionar && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Button
                            size='small'
                            startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => onEdit(club)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, color: 'text.secondary' }}
                          >
                            Editar
                          </Button>
                          <IconButton
                            size='small'
                            onClick={() => onDelete(club)}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          {!isLoading && clubs.length === 0 && (
            <Typography variant='body2' color='text.disabled' sx={{ textAlign: 'center', py: 3 }}>
              {hasFilters ? 'No hay clubes que coincidan con los filtros.' : 'Sin clubes en este campamento.'}
            </Typography>
          )}
          <TablePagination
            component='div'
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage='Filas:'
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Stack>
      )}

      {/* Desktop: Table */}
      {!isMobile && (
      <Paper variant="outlined" sx={{ borderRadius: 1 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell padding="checkbox" sx={{ width: 36 }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    disabled={clubs.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ width: 40, color: 'text.disabled', fontWeight: 600, fontSize: '0.75rem' }}>
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nombre</TableCell>
                <TableCell sx={{ width: 140, fontWeight: 600, fontSize: '0.75rem' }}>Zona</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Director</TableCell>
                <TableCell sx={{ width: 100, fontWeight: 600, fontSize: '0.75rem' }}>Estado</TableCell>
                <TableCell sx={{ width: 110 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : clubs.map((club, idx) => {
                    const isSelected = selected.includes(club._id);
                    const isEditing = selectedId === club._id;
                    const zone =
                      typeof club.affiliatedTo === 'object'
                        ? club.affiliatedTo?.zone
                        : null;

                    return (
                      <TableRow
                        key={club._id}
                        hover
                        selected={isEditing}
                        sx={{
                          bgcolor: isEditing ? 'action.selected' : undefined,
                          opacity: isFetching ? 0.6 : 1,
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleSelectOne(club._id)}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={isEditing ? 600 : 400}
                          >
                            {club.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ZoneBadge zone={zone} affiliations={affiliations} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={club.director ? 'text.primary' : 'text.disabled'}>
                            {club.director || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusDot active={club.status} />
                        </TableCell>
                        <TableCell align="right">
                          {puedeGestionar && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                                onClick={() => onEdit(club)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  py: 0.25,
                                  px: 1,
                                  minWidth: 0,
                                  color: 'text.secondary',
                                }}
                              >
                                Editar
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => onDelete(club)}
                                sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

              {!isLoading && clubs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">
                      {hasFilters ? 'No hay clubes que coincidan con los filtros.' : 'Sin clubes en este campamento.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
        />
      </Paper>
      )}
    </Box>
  );
});
